import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import { DataTable, ExportMenu, FilterBar, type FilterSpec } from "@/components/data";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { applyFilters, parseFilters } from "@/lib/filters";

import { AnomaliesCard } from "./AnomaliesCard";
import { getHistory, getMonitor, getRates, PfcError, type HistoryList, type HistoryMonth, type HistoryView, type Selection } from "./api";
import { atHistoryStart, bandLabel, beforeHistoryStart, clampHistoryMonth, COPY, countryOptions, HISTORY_FLOOR, historyChannel, historySortAllowed, historyStartFor, locationOptions, messageFor, money, monitorOptions, monthsBetween, moveClass, signedMoney, signedPct, sortDirection, visibleMarketplaces } from "./format";
import { historyCsv } from "./forms";
import { HistoryTieSection } from "./HistoryTieSection";
import { SkuHistoryDialog } from "./SkuHistoryDialog";

const BASE_FILTERS: FilterSpec[] = [
  { name: "q", label: "Search", kind: "text" },
  { name: "product", label: "Product", kind: "choice", options: [] },
  { name: "variant", label: "Variant", kind: "choice", options: [] },
  { name: "region", label: "Region", kind: "choice", options: ["UK", "EU", "MENA"].map((value) => ({ value, label: value })) },
  { name: "channel", label: "Channel", kind: "choice", options: ["Amazon", "Shopify", "Wholesale"].map((value) => ({ value, label: value })) },
  { name: "location", label: "Location", kind: "choice", options: [] },
  { name: "country", label: "Ship-to country", kind: "choice", options: [] },
];
function selectedFrom(value: unknown): Selection | null {
  if (!value) return null;
  try { return JSON.parse(String(value)) as Selection; } catch { return null; }
}

export function HistoryTab({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const values = parseFilters(search);
  const today = new Date();
  const thisMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const from = clampHistoryMonth(String(search.h_from ?? HISTORY_FLOOR));
  const to = String(search.h_to ?? thisMonth);
  const view: HistoryView = search.h_view === "change" || search.h_view === "pct" ? search.h_view : "fee";
  const page = Math.max(1, Number(search.h_page ?? 1));
  const selected = selectedFrom(search.h_key);
  const months = monthsBetween(from, to);
  const requestedSort = String(search.h_sort ?? "sku");
  const sort = historySortAllowed(requestedSort, months) ? requestedSort : "sku";
  const direction: "asc" | "desc" = search.h_dir === "desc" ? "desc" : "asc";
  const tooWide = months.length > 36;
  const emptyPeriod = to < from;
  const set = (patch: Record<string, unknown>) => void navigate({ to: ".", search: { ...search, ...patch }, replace: true });

  const rates = useQuery({ queryKey: ["pfc", "rates"], queryFn: getRates, retry: 1 });
  const head = useQuery({ queryKey: ["pfc", "monitor", { country: "ALL", page: 1, sort: "age", direction: "asc" }], queryFn: () => getMonitor({ country: "ALL", page: 1, sort: "age", direction: "asc" }), retry: 1 });
  const list = useQuery({
    queryKey: ["pfc", "history", "list", from, to, values, page, view, sort, direction],
    queryFn: () => getHistory({
      from, to, page, view, sort, dir: direction,
      q: values.q as string, product: values.product as string, variant: values.variant as string,
      region: values.region as string, channel: values.channel as string, location: values.location as string, country: values.country as string,
    }),
    enabled: !tooWide && !emptyPeriod,
    retry: 1,
  });
  if (tooWide) return <p role="alert" className="text-ks-danger">{COPY.historyRange}</p>;
  if (list.error) {
    const error = list.error as PfcError;
    if (error.status === 404) return <p className="text-ks-ink">{COPY.historyOff}</p>;
    return <p role="alert" className="text-ks-danger">{messageFor(error)}</p>;
  }

  const data = list.data?.data as HistoryList | undefined;
  const filters = BASE_FILTERS.map((filter) => {
    if (filter.name === "location") return { ...filter, options: locationOptions(visibleMarketplaces(rates.data?.data.marketplaces ?? [])) };
    if (filter.name === "product" || filter.name === "variant") return { ...filter, options: monitorOptions(head.data?.data.rows ?? [], filter.name) };
    if (filter.name === "country") return { ...filter, options: countryOptions([...(data?.countries ?? [...(data?.keys ?? []).map((key) => key.country), ...(head.data?.data.rows ?? []).map((row) => row.country)]), values.country as string]) };
    return filter;
  });
  const visibleMonths = months;
  type Row = HistoryList["keys"][number];
  const feeCell = (month: HistoryMonth | undefined, currency: string | null) => {
    if (!month) return "—";
    return month.per_unit === null ? "—" : money(month.per_unit, currency);
  };
  const columns: ColumnDef<Row, unknown>[] = [
    { id: "sku", header: "SKU", cell: ({ row }) => {
      const item = row.original;
      return <Button variant="link" data-nonmutating onClick={() => set({ h_key: JSON.stringify({ xero_sku: item.xero_sku, channel_location: item.channel_location, marketplace_id: item.marketplace_id, country: item.country }) })}>{item.xero_sku}</Button>;
    }, meta: { sortable: true } },
    { id: "product", accessorKey: "product", header: "Product", meta: { sortable: true } },
    { id: "variant", accessorKey: "variant", header: "Variant", meta: { sortable: true } },
    { id: "region", header: "Region", cell: ({ row }) => row.original.region ?? "—", meta: { sortable: true } },
    { id: "channel", header: "Channel", cell: ({ row }) => row.original.channel ?? "—", meta: { sortable: true } },
    { id: "location", header: "Location", cell: ({ row }) => row.original.location ?? "—", meta: { sortable: true } },
    { id: "currency", header: "Currency", cell: ({ row }) => row.original.currency ?? "—" },
    ...visibleMonths.map((month): ColumnDef<Row, unknown> => ({ id: month, header: month, cell: ({ row }) => {
      const item = row.original;
      if (beforeHistoryStart(item.channel_location, month)) {
        const reason = COPY.historyBeforeStart(historyChannel(item.channel_location), historyStartFor(item.channel_location));
        return view === "fee" ? <span title={reason}>—</span> : <span title={reason}></span>;
      }
      const first = atHistoryStart(item.channel_location, month);
      const monthIndex = item.months.findIndex((entry) => entry.month === month);
      const value = monthIndex >= 0 ? item.months[monthIndex] : undefined;
      if (view === "fee") return <span className={first ? undefined : moveClass(value?.change)} title={value ? `${value.units} ${item.channel === "Wholesale" ? "cases" : "units"}${value.orders !== null ? `, ${value.orders} orders` : ""}` : undefined}>{feeCell(value, item.currency)}</span>;
      if (first) return <span></span>;
      const displayed = view === "change" ? signedMoney(value?.change, item.currency) : signedPct(value?.change_pct);
      if (!value || displayed === "") return <span></span>;
      const previous = item.months[monthIndex - 1];
      const title = previous ? `${previous.month} ${money(previous.per_unit, item.currency)} to ${month} ${money(value.per_unit, item.currency)}${value.beyond_band ? ` · beyond the ${bandLabel(item.band)} band` : ""}` : undefined;
      return <span className={moveClass(value.change)} title={title}>{displayed}</span>;
    }, meta: { sortable: true } })),
  ];

  const legend = view === "fee" ? COPY.legendFee : view === "change" ? COPY.legendChange : COPY.legendPct;

  return <div className="flex flex-col gap-4">
    <p className="text-sm text-ks-grey-1">{COPY.historyHeader}</p>
    <p className="text-sm text-ks-grey-1">{COPY.historyStarts}</p>
    <div className="flex flex-wrap items-end gap-3">
      <label className="flex flex-col text-sm">From<Input data-nonmutating type="month" min={HISTORY_FLOOR} value={from} onChange={(event) => set({ h_from: event.target.value, h_page: 1 })} /></label>
      <label className="flex flex-col text-sm">To<Input data-nonmutating type="month" value={to} onChange={(event) => set({ h_to: event.target.value, h_page: 1 })} /></label>
      <ExportMenu formats={[{ key: "csv", label: "CSV" }]} decision="view" onExport={() => exportCsv(data, visibleMonths, view)} />
    </div>
    {Boolean(search.h_from) && String(search.h_from) < HISTORY_FLOOR && !emptyPeriod && <p role="status" className="text-sm text-ks-ink">{COPY.historyFloorNotice}</p>}
    <FilterBar filters={filters} values={values} onChange={(next) => void navigate({ to: ".", search: { ...applyFilters(search, next), h_page: 1 }, replace: true })} />
    <div role="radiogroup" aria-label="Cost history view" className="flex flex-wrap gap-2">
      {(["fee", "change", "pct"] as const).map((item) => <Button key={item} variant={view === item ? "default" : "outline"} role="radio" aria-checked={view === item} data-nonmutating onClick={() => set({ h_view: item, h_page: 1 })}>{item === "fee" ? COPY.viewFee : item === "change" ? COPY.viewChange : COPY.viewPct}</Button>)}
    </div>
    <p className="text-sm text-ks-grey-1">{legend} · {COPY.sortHint}</p>
    {emptyPeriod ? <p role="status" className="text-ks-ink">{COPY.historyPeriodEmpty(from, to)}</p> : <DataTable columns={columns} rows={data?.keys ?? []} pending={list.isPending} sort={{ id: sort, direction }} onSort={(id) => set({ h_sort: id, h_dir: sortDirection(sort, direction, id), h_page: 1 })} page={{ pageIndex: page - 1, pageSize: 50, rowCount: data?.total ?? 0 }} onPageChange={(index) => set({ h_page: index + 1 })} emptyMessage={COPY.historyEmpty} caption={`Fulfilment cost history, ${areaName}`} />}
    <Button variant="outline" data-nonmutating onClick={() => set({ h_tie: search.h_tie === "1" ? undefined : "1" })}>{search.h_tie === "1" ? "Hide the P&L tie" : "Tie to the P&L"}</Button>
    {search.h_tie === "1" && <HistoryTieSection thisMonth={thisMonth} />}
    <AnomaliesCard filters={{ channel_location: values.channel_location as string, marketplace_id: values.marketplace_id as string }} runId={head.data?.data.freshness.run_id ?? null} />
    {values.channel !== "Amazon" && values.channel !== "Wholesale" && <AnomaliesCard channel="shopify" filters={{ channel_location: values.channel_location as string, marketplace_id: values.marketplace_id as string }} runId={head.data?.data.freshness.run_id ?? null} />}
    {selected && !emptyPeriod && <SkuHistoryDialog selected={selected} from={from} to={to} thisMonth={thisMonth} onClose={() => set({ h_key: undefined })} />}
  </div>;
}

export function exportCsv(data: HistoryList | undefined, months: string[], view: HistoryView = "fee"): void {
  if (!data) return;
  const blob = new Blob([historyCsv(data, months, view)], { type: "text/csv" });
  const anchor = document.createElement("a");
  anchor.href = URL.createObjectURL(blob);
  anchor.download = `fulfilment-cost-history-${data.from}-${data.to}.csv`;
  anchor.click();
  URL.revokeObjectURL(anchor.href);
}
